import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { companyInfo } from '../../mockData';

const MorenoValleyPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Moreno Valley CA | Legal Document Services | Legal Document Specialist"
        description="Moreno Valley Inland Empire process server. Large-scale residential community service for March Air Reserve Base area, logistics centers, and diverse family neighborhoods. Military and logistics industry expertise."
        keywords="process server Moreno Valley CA, legal document services Moreno Valley, court filing Moreno Valley, skip tracing Moreno Valley, Inland Empire process server"
        canonical="https://legaldocspecialist.com/cities/moreno-valley"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Moreno Valley, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Moreno Valley, CA. Registered and bonded process servers serving the major Inland Empire population center and diverse community of over 215,000 residents.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Population Center Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Moreno Valley Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive legal support services for Moreno Valley's large, diverse Inland Empire community and surrounding areas.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Inland Empire Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Specialized experience serving Moreno Valley's large, diverse population and high-volume service needs.
              </p>
            </div>

            {/* Population Center Advantages */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Users size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Major Population Center Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Large Community Service</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Experience with 215,000+ resident community needs</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Diverse Population Expertise</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Multicultural community service capabilities</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Inland Empire Hub</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Strategic location for regional service coverage</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Deep understanding of Moreno Valley's role as the second-largest city in Riverside County.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Focus */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Moreno Valley?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Large community expertise meets professional standards. We understand Moreno Valley's diverse population and high-volume service requirements.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minMax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Users size={32} />,
                title: 'Large Population Experience',
                description: 'Specialized experience serving major population centers with diverse community needs and high service volumes.'
              },
              {
                icon: <Clock size={32} />,
                title: 'High-Volume Efficiency',
                description: 'Streamlined processes designed for large community service demands with consistent quality standards.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Multicultural Service',
                description: 'Registered, bonded, and experienced with diverse community needs and multicultural service requirements.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Moreno Valley Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Moreno Valley and surrounding Inland Empire communities from our major population center base.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Inland Empire Communities Served from Moreno Valley
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minMax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Moreno Valley', 'Riverside', 'Perris', 'Menifee',
                'Hemet', 'San Jacinto', 'Lake Elsinore', 'Wildomar',
                'Beaumont', 'Banning', 'Cabazon', 'Nuevo'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Complete coverage of central and eastern Riverside County from the major Inland Empire population hub.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Moreno Valley Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for professional service in Moreno Valley and the Inland Empire.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Large community service specialists</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving the Major Inland Empire Hub</strong><br />
                Large Population Expertise • Diverse Community • High-Volume Specialists
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MorenoValleyPage;